import { Directive, ElementRef, inject, Input, OnInit } from '@angular/core';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { of, Subscription } from 'rxjs';
import { delay } from 'rxjs/operators';


/**
* Custom tooltip component to highlight flow steps.
 * @param {string} valueTooltip text of tooltip.
 * @param {boolean} optionalStyle if true set optional style version (i.e. change style).
 * @param {boolean} activeTooltip when true tooltip is opened for some time.
*/
@Directive({
	selector: '[customTooltip]',
	exportAs: 'customTooltip'
})
export class CustomTooltipDirective extends NgbTooltip implements OnInit {

	private readonly _elRef = inject(ElementRef);

	@Input() valueTooltip?: string;
	@Input() optionalStyle?: boolean;
	@Input() set activeTooltip(val: boolean) {
		if (val !== this._active) {
			this._active = val;
			this._active ? this.open() : this.close();
		}
	}

	private delaySubscription$?: Subscription;
	private _active = false;
	private _done = false;
	private readonly OPEN_DELAY: number = 5000;


	constructor() {
		super();
		this.triggers = 'manual';
		this.autoClose = false;
		this.placement = 'right';
		this.tooltipClass = 'custom-tooltip';
	}

	override ngOnInit(): void {
		super.ngOnInit();
		this.optionalStyle && (this.tooltipClass = 'custom-tooltip optional');
		this.ngbTooltip = this.valueTooltip;
		this.shown.subscribe(() => {
			this.addStyle();
			this._done = false;
		});
		this.hidden.subscribe(() => this.removeStyle());
		this.open();
	}

	/**
	* Open tooltip.
	*/
	override open(): void {
		if (!this._done && this._active) {
			super.open();
			this.delaySubscription$ = of(true).pipe(delay(this.OPEN_DELAY)).subscribe(() => this.close());
		}
	}

	/**
	* Close tooltip.
	*/
	override close(): void {
		super.close();
		this.delaySubscription$?.unsubscribe();
	}

	/**
	* Get style for reference element.
	*/
	get style(): string[] {
		return this.optionalStyle ? ['highlighted', 'optional'] : ['highlighted'];
	}

	/**
	* Add style in reference element.
	*/
	addStyle(): void {
		(this._elRef.nativeElement as HTMLDivElement).classList.add(...this.style);
	}

	/**
	* Remove style in reference element.
	*/
	removeStyle(): void {
		(this._elRef.nativeElement as HTMLDivElement).classList.remove(...this.style);
	}


	override ngOnDestroy(): void {
		super.ngOnDestroy();
		this.delaySubscription$?.unsubscribe();
	}

}
